import styled, { css } from 'styled-components';
import palette from '../../../lib/styles/palette';
import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { changeBody } from '../../../modules/conveyor/conveyorWriteSlice';

import { plummerBlocks } from '../../../lib/data/plummerBlock-data';
import { getMinPulleyDia, getPulleyWeight, getShaftDia, standardPulleyDia } from '../../../lib/data/pulley-data-new';
import { formatDisplayValue } from '../../../lib/styles/perttierNumber';
import { MultiInput, MultiOutput, MultiSelect } from '../common/MultiInput';
import AlertLabel from '../../common/AlertLabel';

const DrivePulleyFormDual = ({ pulleys, belt, }) => {
    const dispatch = useDispatch();
    const language = useSelector((state) => state.language.language);
    const isEN = language === 'EN' ? true : false;
    const isJP = language === 'JP' ? true : false;
    
    const beltSpeed = useSelector((state) => state.conveyorWrite.body.input.beltSpeed);
    const beltWidth = useSelector((state) => state.conveyorWrite.body.input.beltWidth);
    const minPulleyDiaByBelt = useSelector((state) => state.conveyorWrite.body.belt?.minPulleyDia);
    const drives = useSelector((state) => state.conveyorWrite.body.drives);


    const [, setDrivePulleys] = useState([]);
    const [nonDrivePulleys, setNonDrivePulleys] = useState([]);
    const [firstPulley, setFirstPulley] = useState(null);
    const [secondPulley, setSecondPulley] = useState(null);

    const brgDiaRankingRef = useRef(
        plummerBlocks.map((item) => ({
            text: `${Number(item.dia).toFixed(1)}`,
            value: item.dia,
        })),
    );

    const standardPulleyDiaRef = useRef(
        standardPulleyDia.map((item) => ({
            text: item,
            value: item,
        })),
    );

    const separateByApplication = (pulleys) => {
        if (!pulleys || !Array.isArray(pulleys) || !pulleys.length) {
            return { drivePulleys: [], nonDrivePulleys: [] };
        }

        const drivePulleys = pulleys.filter((pulley) => pulley?.application?.includes('Drive'));
        const nonDrivePulleys = pulleys.filter(
            (pulley) => pulley?.application && !pulley?.application?.includes('Drive'),
        );

        return { drivePulleys, nonDrivePulleys };
    };

    /* Pulley 폼 초깃값 설정 */
    useEffect(() => {
        const initialfirstPulley = {
            id: 1,
            beltWidth,
            application: '1st Drive',
            pulleyTypeCategory: 'A',
            numberOfPulley: 1,
        };
        const initialsecondPulley = {
            id: 2,
            beltWidth,
            application: '2nd Drive',
            pulleyTypeCategory: 'A',
            numberOfPulley: 1,
        };

        if (!pulleys) {
            // 리덕스 초깃값이 pulleys: null 이므로 즉, pulleys에 아무것도 없으면(처음 풀리를 작성하면)
            setDrivePulleys([initialfirstPulley, initialsecondPulley]);
            setSecondPulley(initialsecondPulley);
            setFirstPulley(initialfirstPulley);
        } else {
            const { drivePulleys, nonDrivePulleys } = separateByApplication(pulleys);
            setDrivePulleys(drivePulleys);
            setNonDrivePulleys(nonDrivePulleys);

            // 풀리 중에 드라이브 풀리가 없는 경우도 있을 수 있으므로
            if (drivePulleys.length > 0) {
                setSecondPulley(drivePulleys[1]);
                setFirstPulley(drivePulleys[0]);
            } else {
                setSecondPulley(initialsecondPulley);
                setFirstPulley(initialfirstPulley);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // 처음 마운트될 때만 실행

    const handleChange = (e) => {
        const { name, value } = e.target;
        const [category, key] = name.split('_');

        if (category === '1st') {
            setFirstPulley((prev) => ({
                ...prev,
                [key]: value,
            }));
        } else if (category === '2nd') {
            setSecondPulley((prev) => ({
                ...prev,
                ...prev[category],
                [key]: value,
            }));
        }
    };

    const handleSelection = (e) => {
        const { name, value } = e.target;
        const [category, key] = name.split('_');

        if (category === '1st') {
            setFirstPulley((prev) => ({
                ...prev,
                [key]: value,
            }));
        } else if (category === '2nd') {
            setSecondPulley((prev) => ({
                ...prev,
                ...prev[category],
                [key]: value,
            }));
        }
    };

    /* 1st Drive - Resultant Force, Torque */
    useEffect(() => {
        if (!belt?.minPulleyDia || !drives[0].tightSideTension || !drives[0].slackSideTension) return;

        const T1 = Number(drives[0]?.tightSideTension?.load);
        const T2 = Number(drives[0]?.slackSideTension?.load);
        const radialForce = T1 + T2;
        const resultantForce = Math.ceil(radialForce * 1.04);

        // 필요 최소 직경을 대략적으로 계산한다.
        const margin = Number(beltWidth) > 1400 ? 150 : 100;
        const minDiaByShapeRatio = (Number(beltWidth) + margin) / 2.5;
        const minDiaByBelt = belt?.minPulleyDia['A'];
        const minPulleyDia = Math.max(minDiaByBelt, minDiaByShapeRatio);

        // Tentative Torque 계산
        const tentativeTorque = Math.ceil(((T1 - T2) * 1.1 * minPulleyDia) / 2 / 10); // N-m => N-cm 단위 환산

        setFirstPulley((prev) => ({
            ...prev,
            resultantForce,
            tentativeTorque,
        }));
    }, [beltWidth, belt, drives, ]);

    /* 2nd Drive - Resultant Force, Torque */
    useEffect(() => {
        if (!belt?.minPulleyDia || !drives[1].tightSideTension || !drives[1].slackSideTension) return;

        const T1 = Number(drives[1]?.tightSideTension?.load);
        const T2 = Number(drives[1]?.slackSideTension?.load);
        const radialForce = T1 + T2;
        const resultantForce = Math.ceil(radialForce * 1.04);

        // 필요 최소 직경을 대략적으로 계산한다.
        const margin = Number(beltWidth) > 1400 ? 150 : 100;
        const minDiaByShapeRatio = (Number(beltWidth) + margin) / 2.5;
        const minDiaByBelt = belt?.minPulleyDia['A'];
        const minPulleyDia = Math.max(minDiaByBelt, minDiaByShapeRatio);

        // Tentative Torque 계산
        const tentativeTorque = Math.ceil(((T1 - T2) * 1.1 * minPulleyDia) / 2 / 10); // N-m => N-cm 단위 환산

        setSecondPulley((prev) => ({
            ...prev,
            resultantForce,
            tentativeTorque,
        }));
    }, [beltWidth, belt, drives, ]);

    /* 1st Drive - Shaft Dia. 계산 */
    useEffect(() => {
        if (!firstPulley?.resultantForce || !firstPulley?.torque || !firstPulley?.eccentricFactor || !firstPulley?.beltWidth ) {
            return;
        }

        const resultantForce = firstPulley?.resultantForce;
        const torque = firstPulley?.torque;
        const eccentricFactor = firstPulley?.eccentricFactor;
        const beltWidth = firstPulley?.beltWidth;
        const isDrive = true;

        const [brgDistance, webDistance, momentArm, maxMoment, hubDiaByStress, hubDiaByDeflection, hubDiaCalculated] =
            getShaftDia(resultantForce, torque, eccentricFactor, beltWidth, isDrive);

        const [, , , , , , brgDiaCalculated] = getShaftDia(
            resultantForce * 0.4,
            torque,
            eccentricFactor,
            beltWidth,
            isDrive,
        );

        setFirstPulley((prev) => ({
            ...prev,
            brgDistance,
            webDistance,
            momentArm,
            maxMoment,
            hubDiaByStress,
            hubDiaByDeflection,
            hubDiaSelected: hubDiaCalculated,
            brgDiaCalculated,
        }));

    }, [firstPulley?.resultantForce, firstPulley?.torque, firstPulley?.eccentricFactor, firstPulley?.beltWidth]);

    /* 2nd Drive - Shaft Dia. 계산 */
    useEffect(() => {
        if (!secondPulley?.resultantForce || !secondPulley?.torque || !secondPulley?.eccentricFactor || !secondPulley?.beltWidth ) {
            return;
        }

        const resultantForce = secondPulley?.resultantForce;
        const torque = secondPulley?.torque;
        const eccentricFactor = secondPulley?.eccentricFactor;
        const beltWidth = secondPulley?.beltWidth;
        const isDrive = true;

        const [brgDistance, webDistance, momentArm, maxMoment, hubDiaByStress, hubDiaByDeflection, hubDiaCalculated] =
            getShaftDia(resultantForce, torque, eccentricFactor, beltWidth, isDrive);

        const [, , , , , , brgDiaCalculated] = getShaftDia(
            resultantForce * 0.4,
            torque,
            eccentricFactor,
            beltWidth,
            isDrive,
        );

        setSecondPulley((prev) => ({
            ...prev,
            brgDistance,
            webDistance,
            momentArm,
            maxMoment,
            hubDiaByStress,
            hubDiaByDeflection,
            hubDiaSelected: hubDiaCalculated,
            brgDiaCalculated,
        }));

    }, [secondPulley?.resultantForce, secondPulley?.torque, secondPulley?.eccentricFactor, secondPulley?.beltWidth]);

    /* 1st Drive - Bearing, PlummerBlock 선정 */
    useEffect(() => {
        if (!firstPulley?.brgDiaSelected) return;
        
        const obj = plummerBlocks.find(({ dia }) => dia >= Number(firstPulley.brgDiaSelected)); // *10은 단위 환산(cm -> mm)

        setFirstPulley((prev) => ({
            ...prev,
            plummerBlockNumber: obj.model,
            plummerBlockWeight: obj.weight,
            brgNumber: obj.bearing,
            dynamicRating: obj.dynamicRating,
        }));
    }, [firstPulley?.brgDiaSelected]);

    /* 2nd Drive - Bearing, PlummerBlock 선정 */
    useEffect(() => {
        if (!secondPulley?.brgDiaSelected) return;
        
        const obj = plummerBlocks.find(({ dia }) => dia >= Number(secondPulley.brgDiaSelected)); // *10은 단위 환산(cm -> mm)

        setSecondPulley((prev) => ({
            ...prev,
            plummerBlockNumber: obj.model,
            plummerBlockWeight: obj.weight,
            brgNumber: obj.bearing,
            dynamicRating: obj.dynamicRating,
        }));
    }, [secondPulley?.brgDiaSelected]);

    /* 1st Drive - Min. Pulley Dia. 계산 */
    useEffect(() => {
        if (!minPulleyDiaByBelt) return;

        const minDiaByBelt = minPulleyDiaByBelt[firstPulley?.pulleyTypeCategory];
        const [minDiaByHubDia, minDiaByShapeRatio] = getMinPulleyDia(
            firstPulley?.beltWidth,
            firstPulley?.hubDiaSelected,
            firstPulley?.pulleyTypeCategory,
        );
        const minPulleyDia = Math.max(minDiaByBelt, minDiaByHubDia, minDiaByShapeRatio);

        setFirstPulley((prev) => ({
            ...prev,
            minPulleyDia,
            minDiaByBelt,
            minDiaByHubDia,
            minDiaByShapeRatio,
        }));
    }, [firstPulley?.beltWidth, firstPulley?.hubDiaSelected, firstPulley?.pulleyTypeCategory, minPulleyDiaByBelt,]);

    /* 2nd Drive - Min. Pulley Dia. 계산 */
    useEffect(() => {
        if (!minPulleyDiaByBelt) return;

        const minDiaByBelt = minPulleyDiaByBelt[secondPulley?.pulleyTypeCategory];
        const [minDiaByHubDia, minDiaByShapeRatio] = getMinPulleyDia(
            secondPulley?.beltWidth,
            secondPulley?.hubDiaSelected,
            secondPulley?.pulleyTypeCategory,
        );
        const minPulleyDia = Math.max(minDiaByBelt, minDiaByHubDia, minDiaByShapeRatio);

        setSecondPulley((prev) => ({
            ...prev,
            minPulleyDia,
            minDiaByBelt,
            minDiaByHubDia,
            minDiaByShapeRatio,
        }));
    }, [secondPulley?.beltWidth, secondPulley?.hubDiaSelected, secondPulley?.pulleyTypeCategory, minPulleyDiaByBelt,]);

    /* 1st Drive - Pulley Dia. with Lagging, RPM, Torque @Pulley Shaft 계산  */
    useEffect(() => {
        if (!firstPulley?.diaSelected || !firstPulley?.laggingThick || !drives[0]?.slackSideTension || !drives[0]?.tightSideTension) return;

        const T1 = Number(drives[0]?.tightSideTension?.load);
        const T2 = Number(drives[0]?.slackSideTension?.load);

        const diaWithLagging = Number(firstPulley.diaSelected) + Number(firstPulley.laggingThick) * 2;
        const rpm = (Number(beltSpeed) * 60) / ((Math.PI * diaWithLagging) / 1000);
        const torqueAtPulleyShaft = (T1 - T2) * diaWithLagging / 2 / 10;    // mm -> cm 단위 환산

        setFirstPulley((prev) => ({
            ...prev,
            diaWithLagging,
            RPM: Math.round(rpm * 10) / 10,
            torqueAtPulleyShaft: Math.ceil(torqueAtPulleyShaft),
        }));
    }, [beltSpeed, drives, firstPulley?.diaSelected, firstPulley?.laggingThick]);

    /* 2nd Drive - Pulley Dia. with Lagging, RPM, Torque @Pulley Shaft 계산  */
    useEffect(() => {
        if (!secondPulley?.diaSelected || !secondPulley?.laggingThick || !drives[1]?.slackSideTension || !drives[1]?.tightSideTension) return;

        const T1 = Number(drives[1]?.tightSideTension?.load);
        const T2 = Number(drives[1]?.slackSideTension?.load);

        const diaWithLagging = Number(secondPulley.diaSelected) + Number(secondPulley.laggingThick) * 2;
        const rpm = (Number(beltSpeed) * 60) / ((Math.PI * diaWithLagging) / 1000);
        const torqueAtPulleyShaft = (T1 - T2) * diaWithLagging / 2 / 10;    // mm -> cm 단위 환산

        setSecondPulley((prev) => ({
            ...prev,
            diaWithLagging,
            RPM: Math.round(rpm * 10) / 10,
            torqueAtPulleyShaft: Math.ceil(torqueAtPulleyShaft),
        }));
    }, [beltSpeed, drives, secondPulley?.diaSelected, secondPulley?.laggingThick]);

    /* 1st Drive - Pulley Weight 계산 */
    useEffect(() => {
        if (
            !firstPulley?.beltWidth ||
            !firstPulley?.hubDiaSelected ||
            !firstPulley?.brgDistance ||
            !firstPulley?.webDistance ||
            !firstPulley?.diaSelected ||
            !firstPulley?.laggingThick
        )
            return;

        const isDrive = true;

        const weight = getPulleyWeight(
            isDrive,
            firstPulley.beltWidth,
            firstPulley.hubDiaSelected,
            firstPulley.brgDistance,
            firstPulley.webDistance,
            firstPulley.diaSelected,
            firstPulley.laggingThick,
        );

        setFirstPulley((prev) => ({
            ...prev,
            weight,
        }));
    }, [
        firstPulley?.beltWidth,
        firstPulley?.hubDiaSelected,
        firstPulley?.brgDistance,
        firstPulley?.webDistance,
        firstPulley?.diaSelected,
        firstPulley?.laggingThick,
    ]);

    /* 2nd Drive - Pulley Weight 계산 */
    useEffect(() => {
        if (
            !secondPulley?.beltWidth ||
            !secondPulley?.hubDiaSelected ||
            !secondPulley?.brgDistance ||
            !secondPulley?.webDistance ||
            !secondPulley?.diaSelected ||
            !secondPulley?.laggingThick
        )
            return;

        const isDrive = true;

        const weight = getPulleyWeight(
            isDrive,
            secondPulley.beltWidth,
            secondPulley.hubDiaSelected,
            secondPulley.brgDistance,
            secondPulley.webDistance,
            secondPulley.diaSelected,
            secondPulley.laggingThick,
        );

        setSecondPulley((prev) => ({
            ...prev,
            weight,
        }));
    }, [
        secondPulley?.beltWidth,
        secondPulley?.hubDiaSelected,
        secondPulley?.brgDistance,
        secondPulley?.webDistance,
        secondPulley?.diaSelected,
        secondPulley?.laggingThick,
    ]);

    /* 리덕스에 쓰기 */
    useEffect(() => {
        if (!firstPulley || !secondPulley) return;

        const newPulleys = [...nonDrivePulleys, firstPulley, secondPulley];

        /* pulleys를 indexArry에 따라 정렬하자. 입려할 때 순서가 바뀔수도 있다. */
        const indexArray = ['Drive', '1st Drive', '2nd Drive', 'Snub', 'Bend', 'Takeup', 'Tail', 'HTBend', 'LTBend'];
        const sortedPulleys = [...newPulleys].sort((a, b) => {
            const indexA = indexArray.indexOf(a?.application);
            const indexB = indexArray.indexOf(b?.application);

            if (indexA === indexB) {
                return a?.id - b?.id; // 동일한 application일 경우 id로 추가 정렬
            }

            // 만약 indexArray에 없는 application이 있다면, 정렬 순서에서 마지막으로 배치
            return (indexA === -1 ? Infinity : indexA) - (indexB === -1 ? Infinity : indexB);
        });

        dispatch(
            changeBody({
                key: 'pulleys',
                value: sortedPulleys,
            }),
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, firstPulley, secondPulley]); // 왜 nonDrivePulleys를 의존성에서 지우니까 무한반복이 사라지나....'24.7/4

    if (!firstPulley || !secondPulley) {
        return;
    }

    return (
        <>
            <MultiOutput
                label="Pulley Application"
                variable="-"
                unit="-"
                variableName="application"
                values={[firstPulley.application, secondPulley.application]}
            />
            <MultiOutput
                label="Pulley Type Category"
                variable="-"
                variableName="pulleyTypeCategory"
                unit="-"
                values={[firstPulley.pulleyTypeCategory, secondPulley.pulleyTypeCategory]}
            />
            <MultiOutput
                label="Qty of Pulley(s)"
                variable="-"
                variableName="numberOfPulley"
                unit="ea"
                values={[firstPulley.numberOfPulley, secondPulley.numberOfPulley]}
            />
            <MultiInput
                label="Resultant Force"
                variable="2U"
                variableName="resultantForce"
                unit="N"
                values={[firstPulley.resultantForce, secondPulley.resultantForce]}
                onChange={handleChange}
            />
            <MultiInput
                label={
                    <AlertLabel
                        label={isEN ? "Tentative Torque @Pulley Shaft" : isJP ? "プーリ軸でのトルク（仮）" : "풀리 샤프트에서의 토크(임시)"}
                        message={isEN ? "First, enter the displayed torque to proceed with the calculation, and then re-enter the value after calculating the 'Torque at the pulley shaft' below." : isJP ? "まず表示されたトルクを入力して計算を進め、下の「プーリ軸でのトルク」が計算された後、その値を再入力してください。" : "우선 표시된 토크를 입력하여 계산을 진행하여 아래 '풀리 축에서의 토크'가 계산된 후 그 값을 재입력하세요."}
                    />
                }
                variable="T"
                variableName="torque"
                unit="N-cm"
                values={[firstPulley.torque, secondPulley.torque]}
                referenceValues={[firstPulley.tentativeTorque, secondPulley.tentativeTorque]}
                onChange={handleChange}
            />
            <MultiInput
                label="Eccentric Factor"
                variable="-"
                variableName="eccentricFactor"
                unit="-"
                values={[firstPulley.eccentricFactor, secondPulley.eccentricFactor]}
                referenceValues={[1.1, 1.1]}
                onChange={handleChange}
            />
            <MultiOutput
                label="Max Moment"
                variable="Mmax"
                variableName="maxMoment"
                unit="N-cm"
                values={[firstPulley.maxMoment, secondPulley.maxMoment]}
            />
            <MultiOutput
                label="Bearing Center Distance"
                variable="L"
                variableName="brgDistance"
                unit="cm"
                values={[firstPulley.brgDistance, secondPulley.brgDistance]}
            />
            <MultiOutput
                label="Web Plate Center Distance"
                variable="L1"
                variableName="webDistance"
                unit="cm"
                values={[firstPulley.webDistance, secondPulley.webDistance]}
            />
            <MultiOutput
                label="Length of Moment Arm"
                variable="L2"
                variableName="momentArm"
                unit="cm"
                values={[firstPulley.momentArm, secondPulley.momentArm]}
            />
            <MultiOutput
                label="Shaft Dia. @Hub by Allowable Stress"
                variable="-"
                variableName="hubDiaByStress"
                unit="cm"
                values={[firstPulley.hubDiaByStress, secondPulley.hubDiaByStress]}
            />
            <MultiOutput
                label="Shaft Dia. @Hub by Allowable Deflection(7')"
                variable="-"
                variableName="hubDiaByDeflection"
                unit="cm"
                values={[firstPulley.hubDiaByDeflection, secondPulley.hubDiaByDeflection]}
            />
            <MultiOutput
                label="Shaft Dia. @Hub Selected"
                variable="-"
                variableName="hubDiaSelected"
                unit="cm"
                values={[firstPulley.hubDiaSelected, secondPulley.hubDiaSelected]}
            />
            <MultiOutput
                label="Shaft Dia. @Bearing Calculated"
                variable="-"
                variableName="brgDiaCalculated"
                unit="cm"
                values={[firstPulley.brgDiaCalculated, secondPulley.brgDiaCalculated]}
            />
            <MultiSelect
                label="Bearing Dia. Selected"
                variable="-"
                variableName="brgDiaSelected"
                unit="cm"
                values={[firstPulley.brgDiaSelected, secondPulley.brgDiaSelected]}
                options={brgDiaRankingRef.current}
                onChange={handleSelection}
                isRequired
            />
            <MultiOutput
                label="Plummer Block Number"
                variable="-"
                variableName="plummerBlockNumber"
                unit="-"
                values={[firstPulley.plummerBlockNumber, secondPulley.plummerBlockNumber]}
            />
            <MultiOutput
                label="Plummer Block Weight"
                variable="Fmax"
                variableName="plummerBlockWeight"
                unit="kg"
                values={[firstPulley.plummerBlockWeight, secondPulley.plummerBlockWeight]}
            />
            <MultiOutput
                label="Bearing Number"
                variable=""
                variableName="brgNumber"
                unit="-"
                values={[firstPulley.brgNumber, secondPulley.brgNumber]}
            />
            <MultiOutput
                label="Dynamic Rating"
                variable=""
                variableName="dynamicRating"
                unit="N"
                values={[firstPulley.dynamicRating, secondPulley.dynamicRating]}
            />
            <MultiOutput
                label="L10h Life"
                variable=""
                variableName="lifeL10h"
                unit="hrs"
                values={[firstPulley.lifeL10h, secondPulley.lifeL10h]}
            />
            <MultiOutput
                label="Min. Pulley Dia. w/o Lagging"
                variable="-"
                unit="mm"
                variableName="minPulleyDia"
                values={[firstPulley.minPulleyDia, secondPulley.minPulleyDia]}
            />
            <MultiOutput
                label="Min. Pulley Dia. by Belt"
                variable="-"
                unit="mm"
                variableName="minDiaByBelt"
                values={[firstPulley.minDiaByBelt, secondPulley.minDiaByBelt]}
                isSub
            />
            <MultiOutput
                label="Min. Pulley Dia. by Hub Dia"
                variable="-"
                unit="mm"
                variableName="minDiaByHubDia"
                values={[firstPulley.minDiaByHubDia, secondPulley.minDiaByHubDia]}
                isSub
            />
            <MultiOutput
                label="Min. Pulley Dia. by Shape Ratio"
                variable="-"
                unit="mm"
                variableName="minDiaByShapeRatio"
                values={[firstPulley.minDiaByShapeRatio, secondPulley.minDiaByShapeRatio]}
                isSub
            />
            <MultiSelect
                label="Pulley Dia. Selected w/o Lagging"
                variable="-"
                variableName="diaSelected"
                unit="mm"
                values={[firstPulley.diaSelected, secondPulley.diaSelected]}
                options={standardPulleyDiaRef.current}
                onChange={handleSelection}
                isRequired
            />
            <MultiInput
                label="Lagging Thickness"
                variable="-"
                unit="mm"
                variableName="laggingThick"
                values={[firstPulley.laggingThick, secondPulley.laggingThick]}
                onChange={handleChange}
            />
            <MultiOutput
                label="Pulley Dia. with Lagging"
                variable="-"
                unit="mm"
                variableName="diaWithLagging"
                values={[firstPulley.diaWithLagging, secondPulley.diaWithLagging]}
            />
            <MultiOutput
                label="Pulley RPM"
                variable="RPM"
                unit="1/min"
                variableName="RPM"
                values={[firstPulley.RPM, secondPulley.RPM]}
            />
            <MultiOutput
                label={isEN ? "Torque At Pulley Shaft" : isJP ? "プーリ軸でのトルク" : "풀리축에 작용하는 토크"}
                variable="-"
                unit="N-cm"
                variableName="torqueAtPulleyShaft"
                values={[firstPulley.torqueAtPulleyShaft, secondPulley.torqueAtPulleyShaft]}
            />
            <MultiOutput
                label="Estimated Pulley Weight"
                variable="-"
                unit="kg"
                variableName="weight"
                values={[firstPulley.weight, secondPulley.weight]}
            />
        </>
    );
};

export default DrivePulleyFormDual;

/* =================   Styles   ================== */
export const Input = styled.input`
    width: 100%;
    box-sizing: border-box;
    border: none;
    outline: none;
    background-color: ${palette.cyan[0]};
    text-align: right;
    // width: 80px;
    padding: 0.25rem 0;
    padding-right: 0.5rem;
    font-size: 0.95rem;

    &::placeholder {
        color: ${palette.lime[5]};
        font-style: italic;
    }

    ${(props) =>
        props.isRequired &&
        css`
            background-color: ${palette.yachtBlue};
            color: white;
        `}
`;

export const RowInput = ({
    label,
    variable,
    unit,
    variableName,
    values,
    min="0",
    referenceValues,
    handleChange,
    isRequired,
}) => {
    return (
        <tr className="tr-hover-effect">
            <td style={{ width: '100%', padding: '0.5rem 1rem' }}>
                <span
                    style={{
                        display: 'block',
                        width: '100%',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                    }}
                >
                    {label}
                </span>
            </td>
            <td style={{ minWidth: '120px', fontSize: '0.9rem', color: `${palette.gray[6]}`, textAlign: 'right' }}>
                {variable}
            </td>
            <td
                style={{
                    minWidth: '120px',
                    textAlign: 'right',
                    paddingRight: '0.5rem',
                    // fontSize: isSub ? '0.85rem' : '0.95rem',
                    // color: isSub ? `${palette.gray[6]}` : null,
                }}
            >
                <Input
                    name={`1st_${variableName}`}
                    value={values[0]}
                    placeholder={referenceValues && referenceValues[0]}
                    // onChange={handleChange}
                    onChange={(e) => {
                        const newValue = e.target.value;
                        // 숫자가 아니거나 'min'보다 작으면 실행하지 않음
                        if (newValue === "" || Number(newValue) >= Number(min)) {
                            handleChange(e);
                        }
                    }}
                    isRequired={isRequired}
                />
            </td>
            <td
                style={{
                    minWidth: '120px',
                    textAlign: 'right',
                    paddingRight: '0.5rem',
                    // fontSize: isSub ? '0.85rem' : '0.95rem',
                    // color: isSub ? `${palette.gray[6]}` : null,
                }}
            >
                <Input
                    name={`2nd_${variableName}`}
                    value={values[1]}
                    placeholder={referenceValues && referenceValues[1]}
                    // onChange={handleChange}
                    onChange={(e) => {
                        const newValue = e.target.value;
                        // 숫자가 아니거나 'min'보다 작으면 실행하지 않음
                        if (newValue === "" || Number(newValue) >= Number(min)) {
                            handleChange(e);
                        }
                    }}
                    isRequired={isRequired}
                />
            </td>
            <td style={unitStyle}>{unit}</td>
        </tr>
    );
};

export const RowOutput = ({ label, variable, unit, values, isSub }) => {
    return (
        <tr className="tr-hover-effect">
            <td style={{ width: '100%', padding: '0.5rem 1rem' }}>
                <span
                    style={{
                        display: 'block',
                        width: '100%',
                        paddingLeft: isSub ? '1rem' : 0,
                        color: isSub ? `${palette.gray[6]}` : null,
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                    }}
                >
                    {label}
                </span>
            </td>
            <td style={variableStyle}>{variable}</td>
            <td
                style={{
                    minWidth: '120px',
                    fontSize: isSub ? '0.85rem' : '0.95rem',
                    textAlign: 'right',
                    paddingRight: '1rem',
                    color: isSub ? `${palette.gray[6]}` : null,
                }}
            >
                {formatDisplayValue(values[0])}
            </td>
            <td
                style={{
                    minWidth: '120px',
                    fontSize: isSub ? '0.85rem' : '0.95rem',
                    textAlign: 'right',
                    paddingRight: '1rem',
                    color: isSub ? `${palette.gray[6]}` : null,
                }}
            >
                {formatDisplayValue(values[1])}
            </td>
            <td style={unitStyle}>{unit}</td>
        </tr>
    );
};

const Select = styled.select`
    width: 100%;
    border: none;
    outline: none;
    padding: 0.25rem 0;
    text-align: center;
    background-color: ${palette.cyan[0]};
    ${(props) =>
        props.isRequired &&
        css`
            background-color: ${palette.yachtBlue};
            color: white;
        `}
`;

export const RowSelect = ({ label, variable, unit, variableName, values, options, handleChange, isRequired }) => {
    return (
        <tr className="tr-hover-effect">
            <td style={{ width: '100%', minWidth: '500px', padding: '0.5rem 1rem' }}>
                <span
                    style={{
                        display: 'block',
                        width: '100%',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                    }}
                >
                    {label}
                </span>
            </td>
            <td style={{ textAlign: 'right' }}>-</td>
            <td style={{ width: '100px' }}>
                <Select name={`1st_${variableName}`} value={values[0]} onChange={handleChange} isRequired={isRequired}>
                    <option value="" disabled hidden>
                        Select...
                    </option>
                    {options.map((item) => (
                        <option value={item.value}>{item.text}</option>
                    ))}
                </Select>
            </td>
            <td style={{ width: '100px' }}>
                <Select name={`2nd_${variableName}`} value={values[1]} onChange={handleChange} isRequired={isRequired}>
                    <option value="" disabled hidden>
                        Select...
                    </option>
                    {options.map((item) => (
                        <option value={item.value}>{item.text}</option>
                    ))}
                </Select>
            </td>
            <td>-</td>
        </tr>
    );
};

const variableStyle = {
    minWidth: '100px',
    fontSize: '0.9rem',
    textAlign: 'right',
    color: `${palette.gray[6]}`,
};

const unitStyle = {
    minWidth: '60px',
    fontSize: '0.9rem',
    color: `${palette.gray[6]}`,
};

export const ErrorMessage = ({ label, variable, unit, values, isSub }) => {
    return (
        <tr>
            <td style={{ maxWidth: '1000px', minWidth: '500px', padding: '0.5rem 1rem' }}>
                <span
                    style={{
                        display: 'block',
                        width: '100%',
                        paddingLeft: isSub ? '1rem' : 0,
                        color: isSub ? `${palette.gray[6]}` : null,
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                    }}
                >
                    {label}
                </span>
            </td>
            <td style={variableStyle}>{variable}</td>
            <td
                colSpan={2}
                style={{
                    width: '120px',
                    // textAlign: 'right',
                    paddingRight: '1.5rem',
                    color: 'red',
                }}
            >
                {(values[0] && values[0]) || (values[1] && values[1])}
            </td>

            <td style={unitStyle}>{unit}</td>
        </tr>
    );
};
